import React, { useEffect, useState } from "react";
import { UserRole } from "./UserRole";
import { CreatePermissionRole } from "./UserRole/CreatePermissionRole";
import { EditPermissionRole } from "./UserRole/EditPermissionRole";
import { UserGroup } from "./UserGroup";
import { showToast } from "services/utils/Status";
import {
  checkEditorPermission,
  checkViewerPermission,
  isProvisioningLoaded,
} from "services/utils/HelperFunction";
import { UserGroupForm } from "./UserGroup/UserGroupForm";
import { UserGroupViewPanel } from "./UserGroup/UserGroupViewPanel";
import { useDispatch, useSelector } from "react-redux";
import { setGlobalSearchValue } from "services/Redux/userToken";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@emotion/react";
import { listHeaderActions, roleAndGroupUi } from "./ui";
import { designTheme } from "services/constants/designTokens";
export const RoleAndUserGroup = () => {
  const [screenNavigation, setScreenNavigation] = useState("");
  const [selectedComponent, setSelectedComponent] = useState("Roles");
  const { userScreenAccess } = useSelector((state) => state.tokenReducer);
  // const [showCreateRole, setShowCreateRole] = useState(false);
  const [editRoleDetails, setEditRowDetails] = useState([]);
  // Add Role opens as a side panel over the Roles list rather than
  // replacing it, so it is separate from screenNavigation.
  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  // Bumped after an Add/Edit Role save: the Roles list refetches in place
  // (no remount, no full-page loader) and an open View Role reloads.
  const [roleEditSavedSignal, setRoleEditSavedSignal] = useState(0);
  // Edit Role opens expanded when clicked from an expanded View Role.
  const [editRoleExpanded, setEditRoleExpanded] = useState(false);
  // User Group view/add/edit all open as side panels over the list:
  // { mode: "view" | "edit" | "create", group }. groupsListKey is bumped
  // after a save so the list refetches in place (no remount, no loader).
  const [groupPanel, setGroupPanel] = useState(null);
  const [groupsListKey, setGroupsListKey] = useState(0);
  // A role clicked in a User Group's "Related User Roles": the screen
  // switches to Roles and UserRole opens View Role for it.
  const [openRoleRequest, setOpenRoleRequest] = useState(null);
  const dispatch = useDispatch();
  // These were previously swapped: the "User Group" tab's create button
  // checked the "Roles" submodule's editor permission, and the "Roles"
  // tab's checked "User Group" instead -- found while wiring in the
  // hide-the-button-if-no-access treatment below, since that meant reading
  // which check actually gates which action. A role with editor on Roles
  // but not User Group (or vice versa) could create the other one anyway.
  const canEditRoles = checkEditorPermission(
    userScreenAccess?.module_details,
    "Roles & User Group",
    "Roles"
  );
  const canEditUserGroup = checkEditorPermission(
    userScreenAccess?.module_details,
    "Roles & User Group",
    "User Group"
  );

  // The "Roles" / "User Groups" tab buttons in the header used to show
  // unconditionally -- a user with view access to only one submodule could
  // still see (and switch to) the tab for the one they can't view at all,
  // which is exactly what UserRole/UserGroup's own hasViewAccess redirect
  // exists to prevent one level down. Hide whichever tab the user can't
  // view, same "hide, don't block" pattern already applied elsewhere.
  const canViewRoles = checkViewerPermission(
    userScreenAccess?.module_details,
    "Roles & User Group",
    "Roles"
  );
  const canViewUserGroup = checkViewerPermission(
    userScreenAccess?.module_details,
    "Roles & User Group",
    "User Group"
  );
  const provisioningLoaded = isProvisioningLoaded(userScreenAccess, "Administration");
  // Default to showing both until provisioning actually resolves, so the
  // tabs don't flash hidden-then-visible on first paint.
  const showRolesTab = !provisioningLoaded || canViewRoles;
  const showUserGroupTab = !provisioningLoaded || canViewUserGroup;

  // The tab to actually render/act on for *this* render -- derived instead
  // of read straight off state. UserRole/UserGroup each run their own
  // hasViewAccess redirect effect one level down, and effects fire
  // child-before-parent within the same commit: if this component ever
  // rendered <UserRole> while selectedComponent was still the stale
  // "Roles" default for a Roles:false/User Group:true user, UserRole's own
  // redirect effect fired first and navigated the user out of
  // Administration entirely, before a post-render effect here ever got a
  // chance to just swap the tab -- which is exactly why a User-Group-only
  // viewer never saw User Group at all. Computing the effective tab at
  // render time means the wrong child is never mounted in the first
  // place, so its redirect effect never runs.
  const effectiveComponent =
    provisioningLoaded &&
    selectedComponent === "Roles" &&
    !canViewRoles &&
    canViewUserGroup
      ? "User Group"
      : provisioningLoaded &&
          selectedComponent === "User Group" &&
          !canViewUserGroup &&
          canViewRoles
        ? "Roles"
        : selectedComponent;

  // Keeps state in sync with the derived value above so a later manual tab
  // click (or a live provisioning change) starts from the corrected tab
  // instead of snapping back to the stale default.
  useEffect(() => {
    if (effectiveComponent !== selectedComponent) {
      setSelectedComponent(effectiveComponent);
    }
  }, [effectiveComponent, selectedComponent]);

  // A handled open-role request is dropped once the user leaves the Roles
  // tab, so coming back later does not reopen that role on remount.
  useEffect(() => {
    if (effectiveComponent !== "Roles") setOpenRoleRequest(null);
  }, [effectiveComponent]);

  const showCreateRoleScreen = () => {
    setCreateRoleOpen(true);
  };

  const showCreateUserGroup = () => {
    setGroupPanel({ mode: "create", group: null });
  };

  const openPrimaryAction = () => {
    if (effectiveComponent === "User Group") {
      if (canEditUserGroup) {
        showCreateUserGroup();
      } else {
        showToast(
          "error",
          "Access denied: You are not authorized to create user groups"
        );
      }
      return;
    }

    if (canEditRoles) {
      showCreateRoleScreen();
    } else {
      showToast(
        "error",
        "Access denied: You are not authorized to create roles"
      );
    }
  };

  return (
    <ThemeProvider theme={designTheme}>
      <Mui.Box sx={roleAndGroupUi.page}>
          {listHeaderActions({
            title:
              effectiveComponent === "User Group" ? "User Groups" : "Roles",
            subtitle:
              effectiveComponent === "User Group"
                ? "Manage user groups and assign company and role access for users."
                : "Define, review, and maintain role permissions across the platform.",
            activeTab: effectiveComponent,
            showRolesTab,
            showUserGroupTab,
            onTabChange: (value) => {
              setSelectedComponent(value);
              dispatch(setGlobalSearchValue(""));
            },
            primaryLabel:
              effectiveComponent === "User Group" ? "Add User Group" : "Add Role",
            // Hide the button rather than show it and block the click --
            // same treatment TeamZone Approve's NewRequestButton already
            // gives its own create action for a non-editor.
            showPrimaryAction:
              effectiveComponent === "User Group" ? canEditUserGroup : canEditRoles,
            onPrimaryClick: openPrimaryAction,
          })}
          {effectiveComponent === "User Group" ? (
            <UserGroup
              refreshSignal={groupsListKey}
              activeGroupId={groupPanel?.mode === "view" ? groupPanel?.group?.id : undefined}
              onViewGroup={(group) => {
                dispatch(setGlobalSearchValue(""));
                setGroupPanel({ mode: "view", group });
              }}
            />
          ) : (
            <UserRole
              setScreenNavigation={setScreenNavigation}
              setEditRowDetails={setEditRowDetails}
              isEditOpen={screenNavigation === "Edit Role"}
              editSavedSignal={roleEditSavedSignal}
              onEditExpandedChange={setEditRoleExpanded}
              editExpanded={editRoleExpanded}
              openRoleRequest={openRoleRequest}
              onOpenUserGroup={(group) => {
                setSelectedComponent("User Group");
                setGroupPanel({ mode: "view", group });
              }}
            />
          )}
          {createRoleOpen && effectiveComponent === "Roles" ? (
            <CreatePermissionRole
              onClose={() => setCreateRoleOpen(false)}
              onSaved={() => setRoleEditSavedSignal((count) => count + 1)}
            />
          ) : null}
          {/* Edit Role opens as a side panel over the list, like Add Role.
              Cancel just closes it; only a successful save reloads the list. */}
          {screenNavigation === "Edit Role" && effectiveComponent === "Roles" ? (
            <EditPermissionRole
              key={editRoleDetails?.role_id ?? "edit"}
              editRoleDetails={editRoleDetails}
              initialExpanded={editRoleExpanded}
              onExpandedChange={setEditRoleExpanded}
              onClose={() => setScreenNavigation("")}
              onSaved={() => setRoleEditSavedSignal((count) => count + 1)}
            />
          ) : null}
          {/* Edit opened from View keeps the view panel mounted but hidden
              (viewGroup), so closing Edit -- outside click, Close or Cancel --
              returns to the view instantly, without a refetch. A successful
              save reloads the list and, when Edit came from View, returns to
              that view with its details refetched (2026-10-01, user request). */}
          {effectiveComponent === "User Group" && groupPanel ? (
            <>
              {groupPanel.mode === "view" || groupPanel.viewGroup ? (
                <UserGroupViewPanel
                  open
                  hidden={groupPanel.mode !== "view"}
                  refreshKey={groupPanel.refreshKey ?? 0}
                  restoreExpanded={groupPanel.expanded}
                  group={groupPanel.viewGroup ?? groupPanel.group}
                  canEdit={canEditUserGroup}
                  onClose={() => setGroupPanel(null)}
                  // Reverse of View Role -> User Group above; only for
                  // users who can view Roles.
                  onOpenRole={
                    canViewRoles
                      ? (role) => {
                          setGroupPanel(null);
                          setSelectedComponent("Roles");
                          setOpenRoleRequest({ role, key: Date.now() });
                        }
                      : undefined
                  }
                  onEdit={(group, options = {}) =>
                    setGroupPanel((prev) => ({
                      mode: "edit",
                      group,
                      // Expanded View -> Edit opens expanded too.
                      expanded: Boolean(options.expanded),
                      viewGroup: prev?.group ?? group,
                      // Carried through Edit so every save bumps it to a new
                      // value and the view refetches, not just the first one.
                      refreshKey: prev?.refreshKey ?? 0,
                    }))
                  }
                />
              ) : null}
              {groupPanel.mode !== "view" ? (
                <UserGroupForm
                  key={`${groupPanel.mode}-${groupPanel.group?.id ?? "new"}`}
                  mode={groupPanel.mode}
                  group={groupPanel.group}
                  initialExpanded={Boolean(groupPanel.expanded)}
                  onExpandedChange={(value) =>
                    setGroupPanel((prev) =>
                      prev && prev.expanded !== value ? { ...prev, expanded: value } : prev,
                    )
                  }
                  onClose={() =>
                    setGroupPanel((prev) => {
                      // UserGroupForm calls onSaved and then onClose. onSaved
                      // has already switched back to the view, so keep it --
                      // this used to see no viewGroup and close everything.
                      if (prev?.mode === "view") return prev;
                      return prev?.viewGroup
                        ? {
                            mode: "view",
                            group: prev.viewGroup,
                            refreshKey: prev.refreshKey ?? 0,
                            expanded: prev.expanded,
                          }
                        : null;
                    })
                  }
                  onSaved={() => {
                    setGroupPanel((prev) =>
                      prev?.viewGroup
                        ? {
                            mode: "view",
                            group: prev.viewGroup,
                            refreshKey: (prev.refreshKey ?? 0) + 1,
                            expanded: prev.expanded,
                          }
                        : null,
                    );
                    setGroupsListKey((key) => key + 1);
                  }}
                />
              ) : null}
            </>
          ) : null}
      </Mui.Box>
    </ThemeProvider>
  );
};
